import type { EmbryoGenomicResult } from "@src/packages/cycles/hooks/useEmbryoReport";
import type { TranslationFunction } from "@src/packages/i18n";

/**
 * An embryo's sex as the record gives it. Typical XX and XY read as a sex;
 * anything else (XXY, 45,X, a mosaic) stays in the lab's own chromosomes.
 */
export type EmbryoSexChromosomes =
  | { kind: "female" }
  | { kind: "male" }
  | { kind: "other"; chromosomes: string };

/** What stands in for the sex chromosomes while she keeps them hidden. */
const HIDDEN_SEX_CHROMOSOMES = "••";

/** Intake answers that record no sex: "Unknown" and "Prefer not to know". */
const NO_SEX_RECORDED = new Set(["unknown", "prefer_not"]);
const FEMALE_VALUES = new Set(["xx", "46,xx", "female", "xx(female)"]);
const MALE_VALUES = new Set(["xy", "46,xy", "male", "xy(male)"]);

/** One "count,sex chromosomes" cell of a karyotype, e.g. "45,X" in a mosaic. */
const KARYOTYPE_CELL = /(\d{2})\s*,\s*([XY]+)(?![A-Za-z])/i;

/** A run of X and Y that names sex chromosomes, not letters inside a word. */
const SEX_CHROMOSOME_RUN = /(^|[^A-Za-z])[XY]+(?![A-Za-z])/gi;

export function isSexChromosome(chromosome: string): boolean {
  return chromosome === "X" || chromosome === "Y";
}

/** Read a recorded sex value, e.g. "xx", "XY", "Female" or "47,XXY". */
export function readEmbryoSexChromosomes(
  value: string | null | undefined,
): EmbryoSexChromosomes | null {
  const recorded = value?.trim();
  if (!recorded) return null;
  const normalized = recorded.toLowerCase().replace(/\s+/g, "");
  if (NO_SEX_RECORDED.has(normalized)) return null;
  if (FEMALE_VALUES.has(normalized)) return { kind: "female" };
  if (MALE_VALUES.has(normalized)) return { kind: "male" };
  return { kind: "other", chromosomes: recorded };
}

/** Each cell line of a karyotype; a mosaic lists several, split by "/". */
function readKaryotypeCells(
  notation: string,
): { count: string; chromosomes: string }[] {
  const cells: { count: string; chromosomes: string }[] = [];
  for (const part of notation.split("/")) {
    const match = KARYOTYPE_CELL.exec(part);
    const count = match?.[1];
    const chromosomes = match?.[2];
    if (count && chromosomes) {
      cells.push({ count, chromosomes: chromosomes.toUpperCase() });
    }
  }
  return cells;
}

/**
 * The sex chromosomes of a tested embryo. The lab notation is the most
 * specific source, so "47,XXY" wins over the report's XX or XY call. A mosaic
 * of sex chromosomes, or a reported change on X or Y, keeps the notation as
 * written. Without a readable notation, the report's call stands.
 */
export function embryoReportSexChromosomes(
  embryo: Pick<
    EmbryoGenomicResult,
    "sex" | "karyotype" | "affectedChromosomes" | "mosaicism"
  >,
): EmbryoSexChromosomes | null {
  const notation = embryo.karyotype.trim();
  const cells = readKaryotypeCells(notation);
  const changedOnSexChromosome = [
    ...(embryo.affectedChromosomes ?? []),
    ...(embryo.mosaicism ?? []).map((mosaic) => mosaic.chromosome),
  ].some(isSexChromosome);
  const cell = cells[0];

  if (!cell) {
    const sex = readEmbryoSexChromosomes(embryo.sex);
    return sex && changedOnSexChromosome
      ? { kind: "other", chromosomes: embryo.sex }
      : sex;
  }
  const mosaicSexChromosomes = cells.some(
    (other) => other.chromosomes !== cell.chromosomes,
  );
  if (mosaicSexChromosomes || changedOnSexChromosome) {
    return { kind: "other", chromosomes: notation };
  }
  if (cell.chromosomes === "XX") return { kind: "female" };
  if (cell.chromosomes === "XY") return { kind: "male" };
  return { kind: "other", chromosomes: `${cell.count},${cell.chromosomes}` };
}

/** The label and value she reads, e.g. "Sex" and "Female (XX)". */
export function describeEmbryoSex(
  sex: EmbryoSexChromosomes,
  t: TranslationFunction,
): { label: string; value: string } {
  switch (sex.kind) {
    case "female":
      return {
        label: t("care.embryo_sex.sex"),
        value: t("care.embryo_sex.female_xx"),
      };
    case "male":
      return {
        label: t("care.embryo_sex.sex"),
        value: t("care.embryo_sex.male_xy"),
      };
    case "other":
      return {
        label: t("care.embryo_sex.sex_chromosomes"),
        value: sex.chromosomes,
      };
  }
}

/**
 * The lab notation with every sex chromosome masked, e.g. "47,••,+21", so the
 * rest of the result still reads while the sex stays hidden. The mask has one
 * length, so it never hints at how many X or Y chromosomes there are.
 */
export function hideSexChromosomes(notation: string): string {
  return notation.replace(SEX_CHROMOSOME_RUN, `$1${HIDDEN_SEX_CHROMOSOMES}`);
}
